import {
  Collapse,
  IconButton,
  IconButtonProps,
  Stack,
  Typography,
  styled,
  Card,
  Box,
  Chip,
} from '@mui/material';
import useCardInformation from '../hooks/useCardInformation';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { useRedirect } from 'react-admin';
import { isEmpty } from 'lodash';
import { apiUrl } from '../constants';
import { number } from 'prop-types';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  height: 202, // 320 / 1.586 aspect ratio for standard credit card
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  position: 'relative',
  border: '1px solid rgba(0, 0, 0, 0.2)',
  padding: '16px 20px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
  fontSize: '0.875rem',
  transformOrigin: 'top center',
  transition: 'transform 0.2s ease',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
}));

const CardChip = styled(Box)(({ theme }) => ({
  width: 36,
  height: 27,
  background: 'linear-gradient(135deg, #FFD700 0%, #FFC000 50%, #FFD700 100%)',
  borderRadius: 3,
  marginTop: 6,
  marginLeft: 10,
  position: 'relative',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
  border: '1px solid rgba(255, 215, 0, 0.5)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      linear-gradient(90deg,
        rgba(255, 255, 255, 0.1) 0%,
        rgba(255, 255, 255, 0.3) 50%,
        rgba(255, 255, 255, 0.1) 100%
      )
    `,
    opacity: 0.9,
  },
  // EMV contact points with rounded edges
  '& > div': {
    position: 'absolute',
    zIndex: 2,
    background: '#FFD700',
    border: '1px solid rgba(255, 215, 0, 0.8)',
    borderRadius: '30%',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background:
        'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 100%)',
      borderRadius: 'inherit',
    },
    '&:nth-of-type(1)': {
      // Top left
      top: '12%',
      left: '10%',
      width: '35%',
      height: '28%',
    },
    '&:nth-of-type(2)': {
      // Top right
      top: '12%',
      right: '10%',
      width: '35%',
      height: '28%',
    },
    '&:nth-of-type(3)': {
      // Middle left
      top: '43%',
      left: '10%',
      width: '35%',
      height: '28%',
    },
    '&:nth-of-type(4)': {
      // Middle right
      top: '43%',
      right: '10%',
      width: '35%',
      height: '28%',
    },
    '&:nth-of-type(5)': {
      // Bottom left
      bottom: '12%',
      left: '10%',
      width: '35%',
      height: '28%',
    },
    '&:nth-of-type(6)': {
      // Bottom right
      bottom: '12%',
      right: '10%',
      width: '35%',
      height: '28%',
    },
  },
}));

const HiddenNumberContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 3,
  backgroundColor: 'rgba(0, 79, 146, 0.2)',
  borderRadius: 5,
  padding: '3px 12px',
  justifyContent: 'center',
  marginLeft: -10,
  height: 24,
}));

interface CardInformation {
  id: string;
  name: string;
  last4Digit: string;
  userId: string;
  frontImageUrl: string;
  bin?: string;
  last4?: string;
  type?: string;
  international?: boolean;
  entity?: string;
  issuer?: string;
  network?: string;
  emi?: boolean;
  sub_type?: string;
  cardStatusId?: number;
  stepCount?: number;
  bankInfo?: {
    logo?: string;
    name?: string;
  };
  cardProvidersInfo?: {
    logo?: string;
    name?: string;
  };
}

interface CardInformationProps {
  selectedCardId: string;
  forceExpanded?: boolean;
  clickable?: boolean;
}

const CardInformation = ({
  selectedCardId,
  forceExpanded,
  clickable,
}: CardInformationProps) => {
  const [expanded, setExpanded] = useState(true);
  const [binApproval, setBinApproval] = useState<boolean | null>(null);
  const [scale, setScale] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const redirect = useRedirect();

  const {
    fetchCardInformation,
    cardInformation,
    cardInformationLoading,
    reset,
  } = useCardInformation();

  const fetchBinApproval = useCallback(async (bin: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/bin/${bin}`,
        {
          headers: {
            Authorization: localStorage.getItem('auth') || '',
          },
        }
      );
      const data = await response.json();

      if (data.code === 'MSG_0047') {
        // BIN doesn't exist in system
        setBinApproval(false);
      } else {
        // BIN exists, check approval status
        setBinApproval(data?.data?.approved || false);
      }
    } catch (error) {
      console.error('Error fetching BIN approval:', error);
      setBinApproval(false);
    }
  }, []);

  useEffect(() => {
    if (cardInformation?.bin && cardInformation?.bin !== 'undefined') {
      fetchBinApproval(cardInformation?.bin);
    }
  }, [cardInformation?.bin, fetchBinApproval]);

  const handleExpandClick = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  // Reset when card changes
  useEffect(() => {
    setExpanded(true);
    reset();
    fetchCardInformation(selectedCardId);
  }, [selectedCardId, reset, fetchCardInformation]);

  // If forceExpanded is true, fetch card info and keep expanded
  useEffect(() => {
    if (forceExpanded) {
      fetchCardInformation(selectedCardId);
      setExpanded(true);
    }
  }, [forceExpanded, selectedCardId, fetchCardInformation]);

  const cardData = useMemo(() => {
    // selectedCardId can be null, so we need to check if cardInformation is null
    return {
      name: cardInformation?.name || 'NO CARD FOUND',
      bin: cardInformation?.bin || '000000',
      last4Digit: cardInformation?.last4Digit || '0000',
      bankLogo: cardInformation?.bankInfo?.logo || null,
      cardProviderLogo: cardInformation?.cardProvidersInfo?.logo || null,
      cardStatusId: (cardInformation as any)?.cardStatusId || null,
      bankName: cardInformation?.bankInfo?.name || 'N/A',
      cardProviderName: cardInformation?.cardProvidersInfo?.name || 'N/A',
      stepCount: (cardInformation as any)?.stepCount,
    };
  }, [cardInformation]);

  // Update scale when container size changes
  useEffect(() => {
    if (!containerRef.current || !expanded) return;

    const updateScale = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const cardWidth = 320; // Natural card width
        const padding = 40; // Account for container padding (20px each side)
        const availableWidth = containerWidth - padding;

        // Calculate scale to fit container, with max scale of 1
        const newScale = Math.min(1, availableWidth / cardWidth);
        setScale(newScale);
      }
    };

    // Initial scale calculation with small delay to ensure DOM is ready
    setTimeout(updateScale, 0);

    // Create ResizeObserver
    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(containerRef.current);

    // Also update on window resize
    window.addEventListener('resize', updateScale);

    // Cleanup
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateScale);
    };
  }, [expanded]);

  return (
    <Stack mt={4} alignItems='center' width='100%'>
      <Stack direction='row' alignItems='center' width='95%' mb={2}>
        {!forceExpanded && (
          <Typography variant='h6'>Card Information</Typography>
        )}
        <ExpandMore
          expand={expanded}
          onClick={handleExpandClick}
          aria-expanded={expanded}
          aria-label='show more'
          disabled={cardInformationLoading}
        >
          <ExpandMoreIcon />
        </ExpandMore>
      </Stack>

      <Collapse
        in={expanded}
        timeout='auto'
        unmountOnExit
        sx={{ width: '100%' }}
      >
        <Stack
          alignItems='center'
          width='100%'
          sx={{ padding: '0 10px' }}
          ref={containerRef}
        >
          {cardInformationLoading ? (
            <Typography>Loading card information...</Typography>
          ) : (
            <Box
              onClick={
                clickable
                  ? () => {
                      redirect('show', 'cards', selectedCardId);
                    }
                  : undefined
              }
              sx={{
                width: '100%',
                margin: '0 auto',
                height: scale < 1 ? 202 * scale + 20 : 222,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                position: 'relative',
                overflow: 'visible',
                cursor: clickable ? 'pointer' : 'default',
              }}
            >
              <StyledCard
                ref={cardRef}
                sx={{
                  transform: `scale(${scale})`,
                }}
              >
                <Box
                  sx={{
                    marginTop: 0.5,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                  }}
                >
                  <Stack
                    direction='column'
                    spacing={0.5}
                    sx={{ width: 'auto' }}
                  >
                    {cardInformation?.bin === 'undefined' ? (
                      <Chip
                        label='Undefined BIN'
                        color='error'
                        size='small'
                        sx={{
                          height: 'auto',
                          fontSize: '0.6em',
                          padding: '0.1em 0.4em',
                          minWidth: 'auto',
                        }}
                      />
                    ) : (
                      !binApproval && (
                        <Chip
                          label='BIN Not Approved'
                          color='error'
                          size='small'
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            padding: '2px 6px',
                            minWidth: 'auto',
                          }}
                        />
                      )
                    )}

                    {/* Card status indicators */}
                    {cardData?.stepCount === 2 &&
                      cardData?.cardStatusId === 2 && (
                        <Chip
                          label='Card Approved'
                          color='success'
                          size='small'
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            padding: '2px 6px',
                            minWidth: 'auto',
                          }}
                        />
                      )}

                    {cardData?.stepCount === 2 &&
                      cardData?.cardStatusId === 3 && (
                        <Chip
                          label='Card Rejected'
                          color='error'
                          size='small'
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            padding: '2px 6px',
                            minWidth: 'auto',
                          }}
                        />
                      )}

                    {cardData?.stepCount === 2 &&
                      cardData?.cardStatusId !== 2 &&
                      cardData?.cardStatusId !== 3 && (
                        <Chip
                          label='Card Not Approved Yet'
                          color='warning'
                          size='small'
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            padding: '2px 6px',
                            minWidth: 'auto',
                          }}
                        />
                      )}

                    {cardData?.stepCount !== 2 &&
                      cardData?.cardStatusId === 2 && (
                        <Chip
                          label='Card Approved'
                          color='success'
                          size='small'
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            padding: '2px 6px',
                            minWidth: 'auto',
                          }}
                        />
                      )}

                    {cardData?.stepCount !== 2 &&
                      cardData?.cardStatusId === 3 && (
                        <Chip
                          label='Card Rejected'
                          color='error'
                          size='small'
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            padding: '2px 6px',
                            minWidth: 'auto',
                          }}
                        />
                      )}

                    {cardData?.stepCount !== 2 &&
                      cardData?.cardStatusId === 1 && (
                        <Chip
                          label={`Step ${Number(cardData?.stepCount) + 1}`}
                          color='info'
                          size='small'
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            padding: '2px 6px',
                            minWidth: 'auto',
                          }}
                        />
                      )}
                  </Stack>
                  {cardData?.bankLogo ? (
                    <img
                      style={{
                        height: '1.8em',
                        width: 'auto',
                        objectFit: 'contain',
                      }}
                      src={cardData.bankLogo}
                      alt='bank logo'
                    />
                  ) : (
                    <CreditCardIcon sx={{ fontSize: '1.8em' }} />
                  )}
                </Box>

                <CardChip>
                  <div /> {/* Top left */}
                  <div /> {/* Top right */}
                  <div /> {/* Middle left */}
                  <div /> {/* Middle right */}
                  <div /> {/* Bottom left */}
                  <div /> {/* Bottom right */}
                </CardChip>

                <Box
                  sx={{
                    border: '1px solid rgba(0, 79, 146, 0.3)',
                    borderRadius: 0.7,
                    p: '2%',
                    height: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-around',
                    fontSize: '1em',
                    minHeight: '2.2em',
                  }}
                >
                  <Typography sx={{ fontSize: '1em', fontWeight: 600 }}>
                    {cardData?.bin?.slice(0, 4) || '0000'}
                  </Typography>
                  <Typography sx={{ fontSize: '1em', fontWeight: 600 }}>
                    {cardData?.bin?.slice(4, 6)}
                  </Typography>
                  <HiddenNumberContainer>
                    {Array(2)
                      .fill('•')
                      .map((dot, i) => (
                        <Typography
                          key={i}
                          sx={{
                            fontSize: '1.1em',
                            color: 'rgba(0, 79, 146, 0.7)',
                            lineHeight: 1,
                            fontWeight: 'bold',
                          }}
                        >
                          {dot}
                        </Typography>
                      ))}
                    {Array(4)
                      .fill('•')
                      .map((dot, i) => (
                        <Typography
                          key={i + 2}
                          sx={{
                            fontSize: '1.1em',
                            color: 'rgba(0, 79, 146, 0.7)',
                            lineHeight: 1,
                            fontWeight: 'bold',
                          }}
                        >
                          {dot}
                        </Typography>
                      ))}
                  </HiddenNumberContainer>
                  <Typography sx={{ fontSize: '1em', fontWeight: 600 }}>
                    {cardData?.last4Digit}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      textTransform: 'uppercase',
                      fontSize: '0.8em',
                      fontWeight: 500,
                      letterSpacing: 0.4,
                    }}
                  >
                    {cardData?.name}
                  </Typography>
                  <Box sx={{ alignSelf: 'flex-end' }}>
                    {cardData?.cardProviderLogo ? (
                      <img
                        style={{
                          height: '1.3em',
                          width: 'auto',
                          objectFit: 'contain',
                        }}
                        src={cardData?.cardProviderLogo}
                        alt='card provider logo'
                      />
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          height: '1.5em',
                        }}
                      >
                        <Box
                          sx={{
                            width: '1.2em',
                            height: '1.2em',
                            borderRadius: '50%',
                            bgcolor: '#EB001B',
                            opacity: 0.9,
                          }}
                        />
                        <Box
                          sx={{
                            width: '1.2em',
                            height: '1.2em',
                            borderRadius: '50%',
                            bgcolor: '#F79E1B',
                            marginLeft: '-0.4em',
                            opacity: 0.9,
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              </StyledCard>
            </Box>
          )}
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default CardInformation;
